import React, { useState } from "react";
import { Building2 } from "lucide-react";
import { Input, Select, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { INDUSTRIES, TIMEZONES } from "./constants";
import { Field, StepShell } from "./_ui";
import type { StepProps } from "./types";

export function StepBusinessProfile({ state, setState, goNext }: StepProps) {
  const bp = (state.business_profile ?? {}) as Record<string, string>;
  const [form, setForm] = useState({
    name: bp.name ?? "",
    industry: bp.industry ?? "",
    timezone: bp.timezone ?? "America/Chicago",
    website: bp.website ?? "",
    primary_email: bp.primary_email ?? "",
    primary_phone: bp.primary_phone ?? "",
    primary_contact: bp.primary_contact ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const valid = form.name.trim().length >= 2 && !!form.industry && !!form.timezone;

  const save = async () => {
    if (!valid) { toast.error("Please add your business name, industry and time zone."); return; }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(), industry: form.industry, timezone: form.timezone,
      };
      if (form.website.trim()) payload.website = form.website.trim();
      if (form.primary_email.trim()) payload.primary_email = form.primary_email.trim();
      if (form.primary_phone.trim()) payload.primary_phone = form.primary_phone.trim();
      if (form.primary_contact.trim()) payload.primary_contact = form.primary_contact.trim();
      const next = await api.saveBusinessProfile(payload);
      setState(next);
      toast.success("Business profile saved");
      goNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepShell
      icon={Building2}
      title="Tell us about your business"
      description="This is how your AI receptionist introduces you to callers."
      onContinue={save}
      saving={saving}
      continueDisabled={!valid}
    >
      <Field label="Business name" required testid="field-name">
        <Input value={form.name} onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. Cool Air HVAC" data-testid="onb-name" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Industry" required testid="field-industry">
          <Select value={form.industry} onChange={(v) => set("industry", v)} options={INDUSTRIES}
            placeholder="Select industry" testid="onb-industry" />
        </Field>
        <Field label="Time zone" required testid="field-timezone">
          <Select value={form.timezone} onChange={(v) => set("timezone", v)} options={TIMEZONES}
            placeholder="Select time zone" testid="onb-timezone" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Main phone" hint="Callers may be transferred here" testid="field-phone">
          <Input value={form.primary_phone} onChange={(e) => set("primary_phone", e.target.value)}
            placeholder="+1 (312) 555-0123" data-testid="onb-phone" />
        </Field>
        <Field label="Contact email" testid="field-email">
          <Input value={form.primary_email} onChange={(e) => set("primary_email", e.target.value)}
            placeholder="hello@yourbusiness.com" data-testid="onb-email" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Website" testid="field-website">
          <Input value={form.website} onChange={(e) => set("website", e.target.value)}
            placeholder="https://yourbusiness.com" data-testid="onb-website" />
        </Field>
        <Field label="Primary contact name" testid="field-contact">
          <Input value={form.primary_contact} onChange={(e) => set("primary_contact", e.target.value)}
            placeholder="Owner or manager" data-testid="onb-contact" />
        </Field>
      </div>
    </StepShell>
  );
}
